var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { hasMorePages, mergePage, nextPageToken } from '@widget/list/Tables/object-search/infinite';
/**
 * Infinite paging state shared by the lists (object search, tables). The caller keeps the page
 * number and calls `load` for it; this hook drops stale replies, appends later pages, and tracks
 * "loading more" and a failed page. The fetch gets the page number and, for token-paged backends,
 * the token of the page before.
 */
export default function useInfinitePaging(options) {
    const { pageSize, idField, setData, setLoading, onFirstPageError } = options;
    const [appending, setAppending] = React.useState(false);
    const [loadError, setLoadError] = React.useState(false);
    const [lastBatch, setLastBatch] = React.useState(0);
    // A request is in flight: no next page is asked for until it is back (a silent reload included)
    const [busy, setBusy] = React.useState(false);
    const requestRef = React.useRef(0);
    const tokenRef = React.useRef(null);
    const loadedRef = React.useRef(0);
    // Callers pass fresh callbacks each render; `load` must stay the same function, or every render
    // would start a new request
    const callbacks = React.useRef({ setData, setLoading, onFirstPageError });
    callbacks.current = { setData, setLoading, onFirstPageError };
    const load = React.useCallback((page_1, fetch_1, ...args_1) => __awaiter(this, [page_1, fetch_1, ...args_1], void 0, function* (page, fetch, silent = false) {
        // A reply to an older request (search or filters changed meanwhile) is dropped
        const req = ++requestRef.current;
        const more = page > 1;
        const { setData, setLoading, onFirstPageError } = callbacks.current;
        if (!more)
            tokenRef.current = null;
        setLoadError(false);
        setBusy(true);
        // A newer request replaces an older one: an older first-page skeleton or "loading more" is
        // turned off here, since the older request's own finish is skipped
        if (more)
            setAppending(true);
        else {
            setAppending(false);
            setLoading === null || setLoading === void 0 ? void 0 : setLoading(!silent);
        }
        try {
            const res = yield fetch(page, tokenRef.current);
            if (req !== requestRef.current)
                return;
            const items = (Array.isArray(res) ? res : res === null || res === void 0 ? void 0 : res.items) || [];
            setData(prev => mergePage(prev, items, page, idField));
            loadedRef.current = (more ? loadedRef.current : 0) + items.length;
            tokenRef.current = nextPageToken(Array.isArray(res) ? null : res === null || res === void 0 ? void 0 : res.pageToken, loadedRef.current);
            setLastBatch(items.length);
        }
        catch (e) {
            if (req !== requestRef.current)
                return;
            if (more)
                setLoadError(true);
            else
                onFirstPageError === null || onFirstPageError === void 0 ? void 0 : onFirstPageError(e);
            console.error('Unable to get data:', e);
        }
        finally {
            if (req === requestRef.current) {
                setBusy(false);
                setAppending(false);
                if (!more && !silent)
                    setLoading === null || setLoading === void 0 ? void 0 : setLoading(false);
            }
        }
    }), [idField]);
    /** Whether to ask for another page: not after a failed page; by the total when known, else a full last page */
    const hasMore = React.useCallback((loaded, total) => !loadError && hasMorePages(loaded, total, lastBatch, pageSize), [loadError, lastBatch, pageSize]);
    return { load, appending, busy, loadError, hasMore, setLastBatch };
}
